import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { Box, Slider, Typography } from '@mui/material';
import { IntlShape } from 'react-intl';

import { SendContext } from '../../SendContext';
import { BaseComponent } from '../Component';
import { MachineContext } from '../../MachineContext';
import { getStringValuesParam, translateWithFallback } from '../../util';
import {EvoachButton} from '../Button';
import { theme as evoachDefaultTheme } from '../../theme/theme';

export interface MultiplePercentageScaleInputComponent extends BaseComponent {
  type: 'multiplePercentageScaleInput';
  props: MultiplePercentageScaleInputProps;
}
export interface MultiplePercentageScaleInputProps {
  buttonText: string;
  scaleNames: string[];
  saveResultTo: string;
  scales?: number[];
  message: string;
  altText: string;
}

/**
 * component MultiplePercentageScaleInput sends two values to the state machine:
 * - <saveResultTo> with an array of values
 * - <saveResultTo>_SUM with a sum of values
 *
 * @returns
 */
export const MultiplePercentageScaleInput: React.FC<MultiplePercentageScaleInputProps> = ({
  buttonText,
  scaleNames,
  scales,
  saveResultTo,
  message,
  altText,
}) => {
  const { send } = useContext(SendContext);
  const marksSize = 100;

  const marks = [
    {
      value: 0,
      label: '0%',
    },
    {
      value: 50,
      label: '50%',
    },
    {
      value: 100,
      label: '100%',
    },
  ];

  // set scales at their max value
  const currentscale = 0; // see comment in PROD-1395, use instaed of Math.floor(marksSize / scaleNames.length);
  const initscales = scaleNames.map(() => currentscale);

  /**
   * calculates the sum of a number array. If localSliderValues is passed, it
   * takes this array, otherwise it takes the sliderValues state
   * @param {number[]} localSliderValues
   * @returns
   */
  const getSumOfSliders = (localSliderValues?: number[]): number => {
    const mySliderValues = localSliderValues ?? sliderValues;
    return mySliderValues.reduce((sum: number, sliderval: number) => {
      return sum + sliderval;
    }, 0);
  };

  const [sliderValues, setSliderValues] = useState<number[]>(
    scales && getSumOfSliders(scales) <= 100 ? scales : initscales
  );

  const [displayMessage, setDisplayMessage] = useState<boolean>(false);

  const handleSliderChange = (newValue: number, index: number) => {
    const clonedValues = cloneDeep(sliderValues);
    clonedValues[index] = newValue as number;

    // update slider only if the sum of all sliders wouldn't exceed 100
    if (getSumOfSliders(clonedValues) > 100) {
      setDisplayMessage(true);
    } else {
      setSliderValues(clonedValues);
      setDisplayMessage(false);
    }
  };

  return (
    <>
      {!displayMessage && (
        <Typography sx={{ paddingLeft: '10px', marginBottom: '20px' }}>
          {message}
        </Typography>
      )}
      {displayMessage && (
        <Typography
          sx={{
            color: evoachDefaultTheme.palette.primary.main,
            paddingLeft: '10px',
            marginBottom: '20px',
          }}
        >
          {altText}
        </Typography>
      )}
      {scaleNames.map((scaleName: string, index: number) => (
        <Box
          sx={{ paddingLeft: '25px', paddingRight: '25px' }}
          key={'boxpercslid' + index}
        >
          <Typography variant="subtitle1">{scaleName}</Typography>
          <div
            style={{
              paddingLeft: '10px',
              paddingRight: '20px',
              paddingTop: '50px',
            }}
          >
            <Slider
              id={'multipercslid' + index}
              value={sliderValues[index]}
              key={'multipercslid' + index}
              step={1}
              valueLabelDisplay="on"
              min={0}
              marks={marks}
              max={marksSize}
              onChange={(_event, newValue: number | number[]) => {
                if (Array.isArray(newValue)) {
                  handleSliderChange(newValue[0], index);
                } else {
                  handleSliderChange(newValue, index);
                }
              }}
              size="medium"
              getAriaValueText={(value: number) => {
                return `${value}%`;
              }}
              valueLabelFormat={(value: number) => {
                return `${value}%`;
              }}
              sx={{
                '& .MuiSlider-valueLabel': {
                  lineHeight: 1.2,
                  fontSize: 12,
                  background: 'unset',
                  padding: 0,
                  width: 37,
                  height: 37,
                  borderRadius: '50% 50% 50% 0',
                  backgroundColor: '#4199AC',
                  transformOrigin: 'bottom left',
                  transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
                  '&:before': { display: 'none' },
                  '&.MuiSlider-valueLabelOpen': {
                    transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
                  },
                  '& > *': {
                    transform: 'rotate(45deg)',
                  },
                },
              }}
            />
          </div>
        </Box>
      ))}

      <EvoachButton
        onClick={() => {
          send({
            type: 'writeToContext',
            payload: { [saveResultTo]: sliderValues },
          });
          send({
            type: 'writeToContext',
            payload: { [saveResultTo + '_SUM']: getSumOfSliders() },
          });
          send({
            type: 'writeToContext',
            payload: {
              [saveResultTo + '_ECHO']: sliderValues.map(
                (sliderValue: number, index: number) =>
                  scaleNames[index] + ' = ' + sliderValue + '%'
              ),
            },
          });
          send({
            type: 'next',
          });
        }}
      >
        {buttonText}
      </EvoachButton>
    </>
  );
};

export const renderMultiplePercentageScaleInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const varsForString: Record<string, any> | undefined = getStringValuesParam(
      payload,
      context
    );

    // if getLabelsfrom is set, take the var as slider names, otherwise take
    // manually entered payload.scalenames. For both values, translation keys
    // are expected to be present
    const scalenames = payload.getLabelsFrom
      ? context.userData[payload.getLabelsFrom]
      : payload.scaleNames.map((scalename: string) =>
          translateWithFallback(intl, scalename, varsForString)
        );

    // if scales are preset, take it
    const scalesPreset = payload.getSeriesFrom
      ? context.userData[payload.getSeriesFrom].flat()
      : undefined;

    newContext.widgetData.push({
      type: 'multiplePercentageScaleInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        buttonText: translateWithFallback(
          intl,
          payload.buttonText,
          varsForString
        ),
        scaleNames: scalenames,
        scales: scalesPreset,
        saveResultTo: payload.saveResultTo,
        message: translateWithFallback(intl, payload.message, varsForString),
        altText: translateWithFallback(intl, payload.altText, varsForString),
      },
    });
    return newContext;
  });
};
